import { Link as RouterLink } from 'react-router-dom';
import { useTranslate } from 'react-admin';
import { Button, Stack, Typography } from '@mui/material';

import { paths } from 'src/routes/paths';
import type { Freeze, QueueList } from 'src/pages/admin/services/vaultOperationsService';

import { OPERATIONS_NS as NS, OUTCOME_BUTTON_SX } from './constants';
import { adminName } from './helpers';
import { EmptyState, ItemCard, ListNotices, TabIntro, TimeText } from './parts';
import type { Queue } from './useQueue';

interface Props {
  queue: Queue<QueueList<Freeze>>;
  vaultName: (vaultId: string | null | undefined) => string;
}

/**
 * "Frozen vaults". Reconciliation & freezes (XBIT-4864: propose a share adjustment, approve it, run reconciliation) stays where it
 * is; every card opens it on THIS vault, so the vault is always an explicit choice and never the first in a list.
 */
export const FrozenTab = ({ queue, vaultName }: Props) => {
  const translate = useTranslate();
  const t = (key: string, options?: object) => translate(`${NS}.frozen.${key}`, options);
  const items = queue.data?.items ?? [];
  return (
    <Stack spacing={2}>
      <TabIntro tab="frozen" />
      {queue.data && (
        <ListNotices
          consistent={queue.data.consistent}
          truncated={queue.data.truncated}
          shown={items.length}
          onRefresh={queue.refetch}
        />
      )}
      {queue.data && items.length === 0 && (
        <EmptyState tab="frozen" checkedAt={queue.data.checkedAt} />
      )}
      {items.map((item) => {
        const vault = vaultName(item.vaultId);
        return (
          <ItemCard
            key={item.vaultId}
            testId={`freeze-${item.vaultId}`}
            title={t('title', { vault })}
            status={item.uxStatus}
            whatHappened={translate(`${NS}.whatHappened.frozen`)}
            affected={t(
              item.subscriptionsFrozen && item.redemptionsFrozen
                ? 'affectedBoth'
                : item.redemptionsFrozen
                  ? 'affectedRedemptions'
                  : 'affectedSubscriptions',
              { vault }
            )}
            extra={
              <Stack spacing={0.75}>
                {item.causes.map((cause) => (
                  <Stack
                    key={`${cause.originVaultId}/${cause.breakValuedAt}/${cause.breakAttemptSeq}`}
                    direction="row"
                    spacing={1}
                    useFlexGap
                    data-testid="freeze-cause"
                    sx={{ alignItems: 'center', flexWrap: 'wrap' }}
                  >
                    <Typography variant="body2">
                      {t('since', { vault: vaultName(cause.originVaultId) })}{' '}
                      <TimeText iso={cause.frozenAt} />.{' '}
                      {!cause.proposal
                        ? t('noProposal')
                        : cause.proposal.status === 'APPLIED'
                          ? t('appliedFix')
                          : cause.proposal.status === 'CANCELLED'
                            ? t('cancelledFix')
                            : cause.proposal.youProposed
                              ? t('youProposed')
                              : t('proposedBy', {
                                  name: adminName(
                                    cause.proposal.proposedByAdmin,
                                    cause.proposal.proposedBy
                                  ),
                                })}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            }
            deadline={t('noDeadline')}
            actions={
              <Button
                sx={OUTCOME_BUTTON_SX}
                variant="contained"
                component={RouterLink}
                to={`${paths.dashboard.vaultReconciliation.root}?vault=${encodeURIComponent(item.vaultId)}`}
              >
                {t('open', { vault })}
              </Button>
            }
            technical={{
              vaultId: item.vaultId,
              subscriptionsFrozen: item.subscriptionsFrozen,
              redemptionsFrozen: item.redemptionsFrozen,
              causes: item.causes.map((x) => ({
                code: x.code,
                scope: x.scope,
                originVaultId: x.originVaultId,
                breakValuedAt: x.breakValuedAt,
                breakAttemptSeq: x.breakAttemptSeq,
                proposalId: x.proposal?.id,
              })),
              asOf: item.asOf,
            }}
          />
        );
      })}
    </Stack>
  );
};
